import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import {
  formatArea,
  formatAreaShort,
} from "components/identifyEventsTool/utils/formatArea";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  ScrapLoadingLayoutResponse,
  useGetScrapLoadingLayoutQuery,
} from "src/store/api/generatedApi";
import { AreaLabels } from "components/scrapEyeLabelling/types";

type ScrapLoadingLayout = ScrapLoadingLayoutResponse["layout"];

const layoutAreas = (layout: ScrapLoadingLayout | undefined) => {
  if (layout === undefined) return [];
  if (layout.layout_type === "railcar")
    return layout.data.tracks.flatMap((track) => track.areas);
  if (layout.layout_type === "grid") return layout.data.areas;
  return [];
};

const areaStrings = (
  layout: ScrapLoadingLayout | undefined,
  pick: (
    area: ReturnType<typeof layoutAreas>[number]
  ) => string | null | undefined
): ReadonlyMap<string, string> =>
  new Map(
    layoutAreas(layout).flatMap((area) => {
      const value = pick(area);
      return value ? [[area.area_id.toLowerCase(), value] as const] : [];
    })
  );

const useAreaLayout = (location: string | undefined) => {
  const tenantName = useTenant();
  const startOfDay = dayjs().startOf("day").toISOString();

  const { data } = useGetScrapLoadingLayoutQuery(
    { tenantName, timestamp: startOfDay, location: location ?? "" },
    { skip: !location }
  );

  return data?.layout;
};

export const useAreaLabel = (
  location: string | undefined
): ((areaId: string | null | undefined) => string) => {
  const { t } = useTenantTranslation();
  const layout = useAreaLayout(location);
  const labels = useMemo(
    () => areaStrings(layout, (area) => area.label),
    [layout]
  );

  return useCallback(
    (areaId: string | null | undefined) => {
      if (!areaId) return "";
      return (
        t(areaId, { defaultValue: "" }) ||
        (labels.get(areaId.toLowerCase()) ?? formatArea(areaId))
      );
    },
    [labels, t]
  );
};

export const useAreaLabelledEvent = (
  location: string | undefined
): (<T extends { area: string | null }>(event: T) => T & AreaLabels) => {
  const resolveAreaLabel = useAreaLabel(location);
  const resolveAreaShortLabel = useAreaShortLabel(location);

  return useCallback(
    <T extends { area: string | null }>(event: T) => ({
      ...event,
      areaLabel: resolveAreaLabel(event.area),
      areaShortLabel: resolveAreaShortLabel(event.area),
    }),
    [resolveAreaLabel, resolveAreaShortLabel]
  );
};

export const useAreaLabelledEvents = (
  location: string | undefined
): (<T extends { area: string | null }>(
  events: readonly T[]
) => (T & AreaLabels)[]) => {
  const withAreaLabels = useAreaLabelledEvent(location);

  return useCallback(
    <T extends { area: string | null }>(events: readonly T[]) => {
      const labelsByArea = new Map<string | null, AreaLabels>();
      return events.map((event) => {
        let labels = labelsByArea.get(event.area);
        if (!labels) {
          const { areaLabel, areaShortLabel } = withAreaLabels({
            area: event.area,
          });
          labels = { areaLabel, areaShortLabel };
          labelsByArea.set(event.area, labels);
        }
        return { ...event, ...labels };
      });
    },
    [withAreaLabels]
  );
};

export const useAreaShortLabel = (
  location: string | undefined
): ((areaId: string | null | undefined) => string) => {
  const layout = useAreaLayout(location);
  const shortLabels = useMemo(
    () =>
      areaStrings(layout, (area) =>
        "short_label" in area ? area.short_label : null
      ),
    [layout]
  );

  return useCallback(
    (areaId: string | null | undefined) => {
      if (!areaId) return "";
      return shortLabels.get(areaId.toLowerCase()) ?? formatAreaShort(areaId);
    },
    [shortLabels]
  );
};
